<template>
  <div class="support">
    <div class="support__info">
      <h2>Need help? We're here for you.</h2>
      <h3>
        <a :href="'tel:' + data['phone']">
          <icon
            class="phone"
            name="phone"
            variant="516173"
          />
          <span>{{ data['phone'] }}</span>
        </a>
      </h3>
      <p>{{ data['hours'] }}</p>
      <h3>
        <a :href="'mailto:' + data['email']">
          <icon
            class="email"
            name="email"
            variant="516173"
          />
          <span>{{ data['email'] }}</span>
        </a>
      </h3>
      <p>Average response time: same day during regular business hours.</p>
    </div>
    <div class="support__form">
      <template v-if="!$store.getters['session/currentUser']">
        <div
          class="support__form__item validate"
          :class="{'validate-error': errors.name}"
        >
          <label for="name">Name</label>
          <input
            v-model="supportForm.name"
            name="name"
            type="text"
          >
          <div class="error-message">
            You must provide your name.
          </div>
        </div>
        <div
          class="support__form__item validate"
          :class="{'validate-error': errors.email}"
        >
          <label for="Email">Email</label>
          <input
            v-model="supportForm.email"
            name="email"
            type="text"
          >
          <div class="error-message">
            You must provide a valid email address.
          </div>
        </div>
      </template>
      <div
        class="support__form__item validate"
        :class="{'validate-error': errors.message}"
      >
        <label for="Message">Message</label>
        <textarea
          v-model="supportForm.message"
          name="message"
          placeholder="Please type your message here and be as descriptive as possible."
          @blur="blur"
          @focus="focus"
        />
        <div class="error-message">
          You must provide a message.
        </div>
      </div>
      <div class="support__form__item submit">
        <button
          :class="{disabled: sending}"
          :disabled="sending"
          @click="sendMessage"
        >
          Send Message
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@components/singletons/icon';

export default {
  components: {
    Icon,
  },
  data() {
    return {
      supportForm: {
        name: '',
        email: '',
        message: '',
      },
      sending: false,
      errors: {
        email: false,
        message: false,
        name: false,
      },
    };
  },
  computed: {
    user() {
      return this.$store.getters['session/currentUser'];
    },
    bundleIdentifier() {
      return this.$store.getters['app/bundleIdentifier'];
    },
    data() {
      const supportData = {
        'com.hltcorp.ase': {
          'phone': '1-703-669-6609',
          'email': 'contactus@ase.com',
          'hours': 'Available 8 a.m.-5 p.m. EST, Monday-Friday (except major U.S. holidays).',
        },
        'default': {
          'phone': '1-319-237-7162',
          'email': 'support@hltcorp.zendesk.com',
          'hours': 'Available 8 a.m.-6 p.m. CST, Monday-Friday (except major U.S. holidays).',
        },
      };
      if ( supportData[this.bundleIdentifier] ) {
        return supportData[this.bundleIdentifier];
      } else {
        return supportData['default'];
      }
    },
  },
  created() {
    // Check if domain is ase.builtbyhlt.com and redirect to ASE contact form
    if (window.location.hostname === 'ase.builtbyhlt.com') {
      window.location.href = 'https://www.ase.com/_contact-form';
      return;
    }

    if (this.user) {
      const fullName = `${this.user.firstName} ${this.user.lastName}`;
      this.supportForm.name = fullName;
      this.supportForm.email = this.user.email;
    }
    if (this.$route.query.message) {
      this.supportForm.message = this.$route.query.message;
      const { query, ...routeSansQuery } = this.$route;
      this.$router.replace(routeSansQuery);
    }
  },
  methods: {
    blur() {
      this.$store.commit('appState/setInputtingText', false);
    },
    focus() {
      this.$store.commit('appState/setInputtingText', true);
    },
    sendMessage() {
      this.validateInput();
      if (this.errors.email || this.errors.message || this.errors.name) {
        this.sending = false;
        return;
      }

      this.sending = true;
      const params = {
        name: this.supportForm.name,
        email: this.supportForm.email,
        message: this.supportForm.message,
      };

      this.$store.dispatch('session/sendSupportMessage', params).then(() => {
        this.$notify({
          group: 'notifications',
          text: 'Your support message has been sent successfully.',
          type: 'success',
        });
      }).catch(() => {
        this.$notify({
          group: 'notifications',
          text: 'There was an error sending your support message. Please use other contact methods to contact support.',
          type: 'error',
        });
      }).finally(() => {
        this.sending = false;
      });
    },
    validateInput() {
      this.errors.email = !/@.+\.\w+$/.test(this.supportForm.email);
      this.errors.message = this.supportForm.message.length < 1;
      this.errors.name = this.supportForm.name.length < 1;
    },
  },
};
</script>

<style lang='scss' scoped>
.support {
  background-color: #eff3f6;
  display: flex;
  @include breakpoint(medium) {
    flex-direction: column;
  }
  & > * {
    flex: 0 0 50%;
    box-sizing: border-box;
    padding: rem-calc(24) rem-calc(38)
  }
  &__info {
    padding-top: 0;
    h2 {
      margin-top: 0;
      font-size: 26px;
      line-height: 36px;
      color: #354052;
    }
    h3 {
      font-size: 22px;
      line-height: 32px;
      a {
        display: flex;
        align-items: center;
        color: #354052;
        width: fit-content;
      }
      .phone {
        width: 24px;
        height: 24px;
        margin-right: 10px;
        transform: scaleX(-1);
      }
      .email {
        width: 24px;
        height: 17px;
        margin-right: 12px;
        margin-top: 3px;
      }
    }
    p {
      font-size: 14px;
      line-height: 21px;
      color: #354052;
      &.main {
        font-size: rem-calc(16);
        line-height: 28px;
      }
    }
  }
  &__form {
    background-color: white;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.25);
    align-self: flex-start;
    @include breakpoint(medium) {
      align-self: auto;
    }
    &__item {
      margin-bottom: rem-calc(30);

      &.validate {
        .error-message {
          margin-top: rem-calc(2);
          height: 16px;
          font-size: rem-calc(12);
          color: red;
          opacity: 0;
        }
        &.validate-error {
          input, textarea {
            border-color: red;
          }
          .error-message {
            opacity: 1;
          }

        }
      }

      &.submit {
        height: 46px;
        button {
          height: 100%;
          padding: rem-calc(4) rem-calc(8);
          font-size: rem-calc(18);
          background-color: #7bd56e;
          width: 100%;
          color: white;
          border: 1px rgba(0, 0, 0, 0.1) solid;
          border-radius: 4px;
          cursor: pointer;
          &.disabled {
            background-color: #b4c9b1;
            cursor: default;
          }
        }
      }

      label, input {
        display: block;
      }
      label {
        color: #7f8fa4;
        font-size: rem-calc(14);
        font-weight: $semi-bold;
        margin-bottom: rem-calc(6)
      }
      input {
        width: 100%;
        padding: 8px;
        border-radius: 4px;
        box-sizing: border-box;
        border: 1px #dfe3e9 solid;
        color: #7f8fa4;

        @include focus-not-visible {
          &:focus {
            border: 1px #7e8083 solid;
            outline: 0;
          }
        }
      }
      textarea {
        width: 100%;
        resize: none;
        height: rem-calc(140);
        border: 1px #dfe3e9 solid;
        border-radius: 4px;
        padding: 8px;
        font-size: rem-calc(14);
        color: #7f8fa4;
        box-sizing: border-box;

        @include focus-not-visible {
          &:focus {
            border: 1px #7e8083 solid;
            outline: 0;
          }
        }
      }
    }
  }
}
</style>
